import React from 'react';

import { useLoopAttachments } from '../pages/loops/LoopAttachments';
import {
  consumePendingChatPrompt,
  consumePendingLoopLaunch,
  consumePendingSkillLaunchPath,
  createSkillUiPath,
  getSkillPromptError,
  savePendingChatPrompt,
  savePendingLoopPrompt,
  savePendingSkillLaunch,
} from '../services/landingSkillRouting';
import type { LoopLaunchOptions, PendingLoopLaunch } from '../services/landingSkillRouting';
import { readPreferredModel } from '../services/modelCatalog';
import { isIOSApp } from '../utils/platformDetection';

import '../styles/variables.css';

import { Footer } from './Footer';
import { GitHubConnectModal } from './GitHubConnectModal';
import {
  DEFAULT_LANDING_CHOICE,
  LandingSkillChoice,
} from './landingChoices';
import { LandingLoopControls } from './LandingLoopControls';
import { handleSendMessage } from './LandingPageHelpers';
import { LandingPageLayout } from './LandingPageLayout';
import { PageMeta } from './PageMeta';
import { useLandingPageState } from './useLandingPageState';

const WEB_META = {
  title: '8080.AI - Build with Scalable AI Agents',
  description: 'Agentic coding that scales to 100M tokens. Build truly scalable Kubernetes-ready projects with AI agents that run for hours and produce production-grade code.',
};

const IOS_META = {
  title: '8080.AI - Plan Apps & Websites with AI Agents',
  description: 'Plan apps and websites with AI agents that help organize requirements, architecture, and project roadmaps.',
};

/* After sign-in, resume whatever the visitor started before the auth modal: a chat prompt or a skill launch. */
const consumePendingHandoff = (): { path: string; state?: PendingLoopLaunch } | null => {
  const pendingLoop = consumePendingLoopLaunch();

  if (pendingLoop !== null) return { path: '/loops', state: pendingLoop };
  const pendingChat = consumePendingChatPrompt();

  if (pendingChat !== null) return { path: '/chat', state: { initialPrompt: pendingChat } };
  const pendingPath = consumePendingSkillLaunchPath();

  return pendingPath ? { path: pendingPath } : null;
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const LandingPage: React.FC = () => {
  const state = useLandingPageState();
  const { isAuthenticated, isLoading: authIsLoading } = state.auth;
  const {
    inputValue,
    setInputValue,
    handleChange: changeInput,
  } = state.inputState;
  const {
    isSubmitting,
    submitError,
    submit,
    onSuccess,
    navigate,
  } = state.messageSubmit;
  // Depend on the stable callback rather than the auth-modal context object,
  // whose identity changes as modal state changes.
  const { openModal } = state.authModal;
  const [githubModalOpen, setGithubModalOpen] = React.useState(false);
  const [skillPromptError, setSkillPromptError] = React.useState<string | null>(null);
  const [selectedSkill, setSelectedSkill] = React.useState<LandingSkillChoice>(DEFAULT_LANDING_CHOICE);
  const loopOptions = React.useRef<LoopLaunchOptions>({ recordVideo: true });
  const setLoopOptions = React.useCallback((options: LoopLaunchOptions) => { loopOptions.current = options; }, []);
  const loopAttachments = useLoopAttachments(undefined, undefined, undefined, undefined, state.auth.user?.id);
  const modelChoice = React.useRef(readPreferredModel());
  const setModelChoice = React.useCallback((model: string) => { modelChoice.current = model; }, []);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    const pending = consumePendingHandoff();

    if (!pending) return;
    changeInput('');
    navigate(pending.path, { replace: true, state: pending.state });
  }, [changeInput, isAuthenticated, navigate]);

  // eslint-disable-next-line max-statements
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldOpen = params.get('github_repo_connect') || sessionStorage.getItem('github_repo_connect_open');

    if (!shouldOpen) return;
    if (authIsLoading) return;

    if (!isAuthenticated) {
      openModal();

      return;
    }

    sessionStorage.removeItem('github_repo_connect_open');
    // Restore the explicit modal request from external URL/session state.
    // eslint-disable-next-line
    setGithubModalOpen(true);
    params.delete('github_repo_connect');
    params.delete('github_error');
    const nextSearch = params.toString();

    window.history.replaceState({}, '', `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`);
  }, [authIsLoading, isAuthenticated, openModal]);

  const visibilityRef = React.useRef<boolean>(true);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as globalThis.CustomEvent<{ isPublic?: boolean }>).detail;

      if (detail?.isPublic !== undefined) {
        visibilityRef.current = detail.isPublic;
      }
    };

    window.addEventListener('landing_visibility_changed', handler);

    return () => window.removeEventListener('landing_visibility_changed', handler);
  }, []);

  const startChat = React.useCallback((text: string) => {
    if (!isAuthenticated) {
      savePendingChatPrompt(text);
      openModal();

      return;
    }
    changeInput('');
    navigate('/chat', { state: { initialPrompt: text.trim() } });
  }, [changeInput, isAuthenticated, navigate, openModal]);

  // eslint-disable-next-line max-statements
  const startLoop = React.useCallback((text: string) => {
    if (loopAttachments.pending) return;
    const options = { ...loopOptions.current, modelId: modelChoice.current,
      attachmentIds: loopAttachments.attachmentIds };

    if (!isAuthenticated) {
      savePendingLoopPrompt(text, options);
      openModal();

      return;
    }
    changeInput('');
    navigate('/loops', { state: { initialPrompt: text.trim(), ...options } });
  }, [changeInput, isAuthenticated, navigate, openModal, loopAttachments.pending, loopAttachments.attachmentIds]);

  // eslint-disable-next-line max-statements
  const handleSendClick = React.useCallback(async (text: string, mediaUrls?: string[]) => {
    if (selectedSkill.kind === 'loop') {
      startLoop(text);

      return;
    }
    if (selectedSkill.kind === 'chat') {
      startChat(text);

      return;
    }
    if (selectedSkill.kind === 'skill') {
      const promptError = getSkillPromptError(text);

      if (promptError) {
        setSkillPromptError(promptError);

        return;
      }
      setSkillPromptError(null);

      if (!isAuthenticated) {
        savePendingSkillLaunch(selectedSkill.id, text);
        openModal();

        return;
      }

      changeInput('');
      navigate(createSkillUiPath(selectedSkill.id), {
        state: { initialPrompt: text.trim() },
      });

      return;
    }

    await handleSendMessage(
      isAuthenticated,
      text,
      mediaUrls,
      openModal,
      submit,
      onSuccess,
      () => setInputValue(''),
      navigate,
      visibilityRef.current,
    );
  }, [
    changeInput,
    isAuthenticated,
    navigate,
    onSuccess,
    openModal,
    selectedSkill,
    setInputValue,
    startChat,
    startLoop,
    submit,
  ]);

  const handleConnectGitHub = React.useCallback(() => {
    if (!isAuthenticated) {
      openModal();

      return;
    }
    setGithubModalOpen(true);
  }, [isAuthenticated, openModal]);

  const handleConnectedProject = React.useCallback((projectId: string) => {
    setGithubModalOpen(false);
    navigate(`/planning/${projectId}`);
  }, [navigate]);

  const handleGithubModalClose = React.useCallback(() => setGithubModalOpen(false), []);
  const handleInputChange = React.useCallback((value: string) => {
    setSkillPromptError(null);
    changeInput(value);
  }, [changeInput]);
  const handleSkillChange = React.useCallback((choice: LandingSkillChoice) => {
    setSkillPromptError(null);
    setSelectedSkill(choice);
  }, []);

  const props = {
    inputValue,
    onInputChange: handleInputChange,
    onSendClick: handleSendClick,
    isSubmitting: isSubmitting || (selectedSkill.kind === 'loop' && loopAttachments.pending),
    submitError: skillPromptError || submitError,
    isAuthenticated,
    authIsLoading,
    selectedSkill,
    onSkillChange: handleSkillChange,
  };
  const meta = isIOSApp() ? IOS_META : WEB_META;

  return (
    <>
      <PageMeta
        title={meta.title}
        description={meta.description}
        path="/"
      />
      <LandingPageLayout {...props} onConnectGitHub={handleConnectGitHub} onModelChange={setModelChoice}
        loopControls={<LandingLoopControls key={state.auth.user?.id || 'anonymous'} authenticated={isAuthenticated}
          disabled={isSubmitting} onChange={setLoopOptions} attachments={loopAttachments} onSignIn={openModal} />} />
      <GitHubConnectModal
        isOpen={githubModalOpen}
        onClose={handleGithubModalClose}
        onConnectedProject={handleConnectedProject}
      />
      <Footer />
    </>
  );
};
